import config from '../../../share/config';
import handler from '../../../share/util/apiResultValidator';
import ErrorCodes from '../../../common/nodejs/errors';
import errorMapping from "../../../share/util/api/errorMapping";
import { VEHICLE_INFO_GET } from './constants.js';
import util from '../../../share/util/utils';
import apiService from '../../../share/util/api/service.js';
import constants from '../../../common/nodejs/constants';
import { ALL_CUSTOMIZE } from "../../../constants/usageCountTypes";
import alert from "../../../share/alert";

/**
 * APIの戻り値をチェックする
 * @param {object} root 呼び出し元
 * @param {object} data 
 * @param {string?} message
 * @returns {boolean} true: エラー有り
 */
const checkError = (root, data, message = 'message.error_vehicle_connect_title') => {
  if (data.errorResult) {
    if (data.errorResult.causeType === handler.causeTypes.permissionError) {
      // ユーザー権限エラーの場合、エラーダイアログを表示しログアウトする
      alert.showError(root, null, root.$t(errorMapping.getErrorMessageForCode(ErrorCodes.USER_ACCESS_PERMISSION_ERROR)),
        () => root.logout(root)
      );
    } else if (data.errorResult.behaviorType === handler.behaviorTypes.logout) {
      root.logout(root);
    }
    else {
      // 予期せぬエラーで個別診断Menuが取得出来なかった際の処理
      alert.showError(root, null, root.$t(message));
    }
    return true;
  }
  return false;
};

export const vehicleFuncs = {
  /**
   * 車両情報取得(診断)の状態監視
   * 使用時に必ず使用元のコンポーネントをバインドすること {関数名}.bind(this,...)
   * @param {string} value 車両情報取得(診断)の状態
   * @param {() => Promise<any>} succeedFunc
   * @returns {Promise<void>}
   */
  vehicleStatus: async function (value, succeedFunc = async () => { }) {
    // 失敗
    if (value === config.POLLING_FAILURE) {
      this.vehicleRunStatus = null;
      this.processing = false;
      const error = this.vehicleError;
      // VINの状態が全取得時と異なっていた場合、警告ダイアログ表示
      if (error.validatorResult?.causeType === handler.causeTypes.differentVinError) {
        // 車両情報取得処理停止 
        this.$stopInterval(this.vehicleWatcher);
        // 取得した情報のVIN番号が異なる場合 
        const messages = this.$t(error.message);
        alert.showWarning(this, null, messages, () => {
          // 車両情報が異なるためエラーとして start 画面に戻す 
          this.$router.push({ name: 'home', params: { isAuto: true, isConfirmSkip: true } });
          return;
        });
        return;
      }
      // APIバリデータの戻りを確認
      if (error.validatorResult) {
        // ネットワーク接続エラーとセッション情報エラーは警告メッセージで表示する
        if (error.validatorResult.causeType === handler.causeTypes.networkError ||
          error.validatorResult.causeType === handler.causeTypes.invalidSessionToken) {
          alert.showWarning(this, null, error.message, () => {
            if (error.behaviorType === handler.behaviorTypes.logout) {
              this.logout(this);
            }
            else if (error.behaviorType === handler.behaviorTypes.resetSession) {
              this.resetSession(this);
            }
          });
          return;
        }
        // 警告以外のエラーメッセージの表示
        vehicleFuncs.showFailedMessage.bind(this, 'message.error_vehicle_connect_title', this.vehicleError)();
      }
      else {
        // バリデータ情報がない場合はエラーメッセージを表示する
        vehicleFuncs.showFailedMessage.bind(this, 'message.error_vehicle_connect_title', this.vehicleError)();
      }
    }
    // 成功
    else if (value === config.POLLING_COMPLETED) {
      try {
        const projectInfo = {
          projectId: this.vehicleInfo.vehicle.project.id,
          measureId: this.vehicleInfo.vehicle.project.all_dtc.measure_id,
          operationScreen: this.operationScreen
        };

        if (projectInfo.projectId === null || projectInfo.measureId === null) {
          alert.showError(this, null, this.$t('message.error_vehicle_connect_title'));
          return;
        }

        // 個別診断機能が利用可能な場合のみ個別診断メニュー一覧, 複数診断メニュー一覧を取得する
        if (this.isDiagAvailable) {
          const individualMenuData = await this.getIndividualMenus({
            netAppId: this.netAppId,
            modal: this
          });
          if (checkError(this, individualMenuData)) return;

          const multiMenuData = await this.getMultipleMenus({
            netAppId: this.netAppId,
            modal: this
          });
          if (checkError(this, multiMenuData)) return;
        }

        const dtcTableResponse = await this.getDtcSystemTable(projectInfo);
        if (checkError(this, dtcTableResponse)) return;
        if (this.vehicleRunStatus === VEHICLE_INFO_GET) {
          await util.sleep(1000);
          // 取得した情報のVIN番号が異なる場合 
          if (this.vehicleInfo.vehicle.is_vin_changed === true) {
            const messages = this.$t('message.message_change_vehicle_info_error');
            alert.showWarning(this, null, messages, () => {
              // 車両情報が異なるためエラーとして start 画面に戻す 
              this.$router.push({ name: 'home', params: { isAuto: true, isConfirmSkip: true } });
              return;
            });
          } else {
            // 診断情報取得のカウントを１つアップする。 
            this.usageCountUp({ type: ALL_CUSTOMIZE });
            // 成功時関数を実施する
            await succeedFunc();
          }
        }
        this.vehicleRunStatus = null;
      } finally {
        this.processing = false;
      }
    }
  },
  /**
   * 車両情報取得(診断)ステータスの状態監視
   * 使用時に必ず使用元のコンポーネントをバインドすること {関数名}.bind(this,...)
   * @param {string} value 車両情報取得(診断)ステータスの状態
   * @returns {Promise<void>}
   */
  phase: async function (value) {
    // VIN情報・車台番号が取得できない場合 
    if (value === config.COMMUNICATION_UI_VIN_REQUIRED) {
      // 車両番号が同一の場合は入力画面を表示せず、VIN 更新
      if (this.oldVehicleInfo) {
        if (this.vehicleInfo.vehicle.vin.vin === this.oldVehicleInfo.vehicle.vin.vin) {
          // 本APIの失敗はvehicleStatusに格納される 
          await this.runSystemApi({ netAppId: this.netAppId, this: this, type: apiService.DIAG });
        } else {
          // 車両情報取得処理停止 
          this.$stopInterval(this.vehicleWatcher);
          this.processing = false;
          // 取得した情報のVIN番号が異なる場合 
          const messages = this.$t('message.message_change_vehicle_info_error');
          alert.showWarning(this, null, messages, () => {
            // 車両情報が異なるためエラーとして start 画面に戻す 
            this.$router.push({ name: 'home', params: { isAuto: true, isConfirmSkip: true } });
            return;
          });
        }
      }
    } else if (value === config.COMMUNICATION_UI_CGW_CECU_NOT_COMMUNICATED) {
      // CGW/C-ECU通信無しの場合
      this.processing = false;
      this.modalAlertCgwCecuNotCommunicated = true;
    } else if (value === config.COMMUNICATION_UI_CONFIRM_READ_LEGISLATED_SUPPORT_IDS) {
      // リアルワールドデータ手動取得の場合
      // 全カスタマイズの場合は、確認ダイアログを出さず、is_consent_obfcm: false(OBFCM取得しない) でAPI実行のみ行う
      await this.getObfcmToVehicle();
    }
  },

  /**
   * 車両通信ポーリングの再開処理（OBFCM取得しない）
   * 使用時に必ず使用元のコンポーネントをバインドすること {関数名}.bind(this,...)
   * @returns {Promise<void>}
   */
  getObfcmToVehicle: async function () {
    const data = {
      this: this,
      netAppId: this.netAppId,
      requestBody: {
        type: constants.VEHICLE_CONTINUE_CHECK_SYSTEM_TYPE_GET_OBFCM,
        is_consent_obfcm: false
      }
    };
    // SystemsAPIのCGWサポートチェック後の処理を実行
    const isSuccess = await this.continueSystemApi(data);

    // 正常終了か確認
    if (isSuccess) {
      // 車両通信ポーリングを再開
      this.$resumeInterval(this.vehicleWatcher);
    }
    else {
      // ポーリングの停止
      this.$stopInterval(this.vehicleWatcher);
    }
  },

  /**
   * CGW/C-ECU通信無し警告ダイアログからの車両通信ポーリングの再開処理
   * 使用時に必ず使用元のコンポーネントをバインドすること {関数名}.bind(this,...)
   * @returns {Promise<void>}
   */
  restartConnectionToVehicle: async function () {
    const data = {
      this: this,
      netAppId: this.netAppId,
      requestBody: {
        type: constants.VEHICLE_CONTINUE_CHECK_SYSTEM_TYPE_CGW_CECU_NOT_COMMUNICATED
      }
    };
    this.processing = true;
    // SystemsAPIのCGWサポートチェック後の処理を実行
    const isSuccess = await this.continueSystemApi(data);
    this.processing = false;

    // 正常終了か確認
    if (isSuccess) {
      // CGW/C-ECU通信無し警告ダイアログを閉じて車両通信ポーリングを再開
      this.modalAlertCgwCecuNotCommunicated = false;
      this.processing = true;
      this.$resumeInterval(this.vehicleWatcher);
    }
    else {
      // ポーリングの停止
      this.$stopInterval(this.vehicleWatcher);
    }
  },

  /**
   * エラーメッセージ表示処理 
   * 使用時に必ず使用元のコンポーネントをバインドすること {関数名}.bind(this,...)
   * @param {string} errTitleId 
   * @param {object} errorInfo 
   * @param {boolean} isLogout 
   */
  showFailedMessage(errTitleId, errorInfo, isLogout = true) {
    const error = errorInfo;
    // メンテナンスエラーの場合はエラー表示を変更する 
    let messages = null;
    if (!error.validatorResult || error.validatorResult.causeType === handler.causeTypes.maintenanceError) {
      messages = error.message;
    }
    else {
      const errTitle = this.$t(errTitleId);
      messages = {
        mainMessage: errTitle,
        messageList: []
      };
      const messageFirst = {
        label: this.$t('message.common_label_error_message'),
        message: error.message
      };
      messages.messageList.push(messageFirst);
    }

    alert.showError(this, null, messages, () => {
      if (error.behaviorType === handler.behaviorTypes.logout) {
        if (isLogout) {
          this.logout(this);
        }
      }
      this.modalDelete = false;
    });
  }
};